/**
 * How a person's name reads on screen.
 *
 * Names reach this app from two places that spell the same person differently.
 * `ContactPersonal.PersonInfo.Name.FullName` is the bare name — "Iggy Sandejas"
 * — with the salutation held separately. `School.Contact.name.full_name`, which
 * the token carries and the publish stamps fall back to, prefixes it: "Mr Iggy
 * Sandejas".
 *
 * That prefix is not merely untidy. Avatar initials are taken from the first and
 * last word, so "Mr Iggy Sandejas" initials to MS — someone else's initials
 * entirely, sitting next to their name.
 */

/**
 * Titles dropped from the front of a name.
 *
 * A closed list, matched only at the START and only when something follows.
 * Anything cleverer risks eating a real name: Dr is a title, Drew is not.
 */
const SALUTATIONS = new Set([
	"mr",
	"mrs",
	"ms",
	"miss",
	"mx",
	"dr",
	"prof",
	"professor",
	"rev",
	"reverend",
	"fr",
	"sr",
	"br",
	"sir",
	"dame",
	"lady",
]);

/**
 * The name without its salutation.
 *
 * Returns the input untouched when there is nothing to strip, when stripping
 * would leave nothing, or when the value is not a name at all — a caller
 * passing a role name through this must get the role name back.
 */
export function personName(value: string | undefined | null): string {
	const text = (value ?? "").trim();
	if (!text) return "";
	const parts = text.split(/\s+/);
	if (parts.length < 2) return text;
	const first = parts[0].replace(/\.$/, "").toLowerCase();
	return SALUTATIONS.has(first) ? parts.slice(1).join(" ") : text;
}

/**
 * The name, or nothing when nobody knows it.
 *
 * The API stamps the literal string "Unknown" when it cannot resolve a person,
 * and that is not a name — printed as one it becomes "Changes requested by
 * Unknown", which reads worse than saying nothing about who.
 */
export function knownName(value: string | undefined | null): string | null {
	const name = personName(value);
	return !name || name.toLowerCase() === "unknown" ? null : name;
}
